import { FanResponseDto, type OnlyFansList } from '@api'
import qs from 'query-string'

import type { Creator } from '@/modules/creators/types'
import { apiClientChatting } from '@/utils/axios'

import type {
  EditPinnedListStateRequestOptions,
  GetBlockedUsersOptions,
  GetFollowingUsersOptions,
  GetListUsersOptions,
  GetListUsersResponse,
  GetListsUsersCountResponse,
  GetRestrictedUsersOptions,
  GetSpendingHistoryResponse,
  GetSubscribersListOptions,
  ListCreationProgress,
  ListOrderOptions,
  ListSortOptions,
  UpdateListsUserRequestOptions,
} from '../types'

const renameList = ({
  creatorId,
  listId,
  newName,
}: {
  creatorId: number
  listId: OnlyFansList['id']
  newName: string
}) =>
  apiClientChatting.patch<void>(
    `/of-lists/${creatorId}/lists/${listId}/rename`,
    {
      newName,
    },
  )

const clearList = ({
  creatorId,
  listId,
}: {
  creatorId: number
  listId: string
}) =>
  apiClientChatting.post<void>(
    `/of-collections/${creatorId}/list/${listId}/clearList`,
  )

const deleteList = ({
  creatorId,
  listId,
}: {
  creatorId: number
  listId: string
}) => apiClientChatting.delete<void>(`/of-lists/${creatorId}/${listId}`)

const getUsers = ({
  creatorId,
  listId,
  ...queryParams
}: GetListUsersOptions) => {
  const queryString = qs.stringify(
    { ...queryParams, type: 'all' },
    {
      skipNull: true,
    },
  )

  return apiClientChatting
    .get<GetListUsersResponse>(
      `/of-collections/${creatorId}/list/${listId}/users?${queryString}`,
    )
    .then((res) => ({
      ...res.data,
      pinned: queryParams.pinned,
      list: res.data.list.map((user) => ({
        ...user,
        isPinned: queryParams.pinned,
      })),
    }))
}

const getSubscribers = ({
  creatorId,
  offset,
  type,
  query,
  filters,
}: GetSubscribersListOptions) => {
  const queryString = qs.stringify(
    { offset, type, query },
    {
      skipNull: true,
    },
  )

  return apiClientChatting
    .post<GetListUsersResponse>(
      `/of-collections/${creatorId}/subscribers?${queryString}`,
      {
        ...filters,
        recommenders: filters.recommenders && Number(filters.recommenders),
        online: filters.online && Number(filters.online),
      },
    )
    .then((res) => res.data)
}

const getRestrictedUsers = ({
  creatorId,
  offset,
  query,
}: GetRestrictedUsersOptions) => {
  const queryString = qs.stringify(
    { offset, query },
    {
      skipNull: true,
    },
  )

  return apiClientChatting
    .post<GetListUsersResponse>(
      `/of-collections/${creatorId}/restricted?${queryString}`,
    )
    .then((res) => res.data)
}

const getFollowingUsers = ({
  creatorId,
  offset,
  query,
  sort,
  order,
  type,
  filters,
}: GetFollowingUsersOptions) => {
  const queryString = qs.stringify(
    { offset, query, sort, order, type },
    {
      skipNull: true,
    },
  )

  return apiClientChatting
    .post<GetListUsersResponse>(
      `/of-collections/${creatorId}/subscribes?${queryString}`,
      filters,
    )
    .then((res) => res.data)
}

const getBlockedUsers = ({
  creatorId,
  query,
  offset,
  order,
  sort,
}: GetBlockedUsersOptions) => {
  const queryString = qs.stringify(
    { offset, query, sort, order },
    {
      skipNull: true,
    },
  )

  return apiClientChatting
    .post<GetListUsersResponse>(
      `/of-collections/${creatorId}/blocked?${queryString}`,
    )
    .then((res) => res.data)
}

const getSubscribersSpendingHistory = ({
  creatorId,
  userId,
  offset,
}: {
  creatorId: Creator['id']
  userId: FanResponseDto['id']
  offset: number
}) => {
  return apiClientChatting
    .get<GetSpendingHistoryResponse>(
      `/of-collections/${creatorId}/subscriptions/${userId}/history?offset=${offset}`,
    )
    .then((res) => res.data)
}

const getListUsersCount = ({ creatorId }: { creatorId: Creator['id'] }) => {
  return apiClientChatting
    .get<GetListsUsersCountResponse>(`/of-collections/${creatorId}/count`)
    .then((res) => res.data)
}

const addUsers = ({
  creatorId,
  listId,
  ids,
}: {
  creatorId: Creator['id']
  listId: OnlyFansList['id']
  ids: FanResponseDto['id'][]
}) => {
  return apiClientChatting
    .post<
      Promise<void>
    >(`/of-collections/${creatorId}/list/${listId}/addUsersBatch`, ids)
    .then((res) => res.data)
}

export const updateUserInLists = ({
  creatorId,
  userId,
  request,
}: {
  creatorId: number
  userId: FanResponseDto['id']
  request: UpdateListsUserRequestOptions
}): Promise<void> => {
  return apiClientChatting.put(
    `/of-lists/${creatorId}/users/${userId}`,
    request,
  )
}

const pinUser = ({
  creatorId,
  listId,
  userId,
}: {
  creatorId: Creator['id']
  listId: OnlyFansList['id']
  userId: FanResponseDto['id']
}) => {
  return apiClientChatting.post<void>(
    `/of-collections/${creatorId}/list/${listId}/pin/${userId}`,
  )
}

export const editPinnedListState = ({
  creatorId,
  listsToUpdate,
}: EditPinnedListStateRequestOptions): Promise<void> => {
  return apiClientChatting.post(`/of-lists/${creatorId}/setPinned`, {
    listsToUpdate,
  })
}

const getListCreationProgress = (
  creatorId: number,
  listId: number,
): Promise<ListCreationProgress> => {
  return apiClientChatting
    .get(`/lists/${creatorId}/${listId}/status`)
    .then((res) => res.data)
}

const saveCustomOrder = (
  creatorId: Creator['id'],
  order: OnlyFansList['id'][],
) => {
  return apiClientChatting.post<void>(
    `/of-collections/${creatorId}/list/setCustomOrder`,
    order,
  )
}

const setSortAndOrder = (
  creatorId: Creator['id'],
  sort?: ListSortOptions,
  order?: ListOrderOptions,
) => {
  const query = qs.stringifyUrl(
    {
      url: `/of-lists/${creatorId}/setOrder`,
      query: {
        sort,
        order,
      },
    },
    {
      skipEmptyString: true,
      skipNull: true,
    },
  )

  return apiClientChatting.post(query.toString())
}

export const listsService = {
  getUsers,
  getSubscribers,
  getSubscribersSpendingHistory,
  getRestrictedUsers,
  getBlockedUsers,
  getFollowingUsers,
  getListUsersCount,
  addUsers,
  renameList,
  clearList,
  deleteList,
  updateUserInLists,
  pinUser,
  editPinnedListState,
  getListCreationProgress,
  saveCustomOrder,
  setSortAndOrder,
}
